/**
 * The avatar and its menu: Profile, Change password, Sign out (confirmed
 * first). At the foot
 * of the rail on desktop, at the end of the top bar elsewhere.
 */

import { ChevronsUpDown, KeyRound, LogOut, Settings, UserCircle2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { Dropdown, type DropdownItem } from "@/primitives/overlays";
import { Avatar } from "@/primitives/ui/Avatar";
import { ROUTES } from "@/types/routes";

import { SignOutDialog } from "./SignOutDialog";

export function displayNameOf(
  user: ReturnType<typeof useAuth>["user"],
): string {
  return (
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "Account"
  );
}

export function AccountMenu({
  variant,
}: {
  variant: "side" | "bar";
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const name = displayNameOf(user);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const items = useMemo<DropdownItem[]>(
    () => [
      {
        id: "profile",
        label: "Profile",
        icon: UserCircle2,
        onSelect: () => navigate(ROUTES.PROFILE),
      },
      {
        id: "password",
        label: "Change password",
        icon: KeyRound,
        onSelect: () => navigate(ROUTES.CHANGE_PASSWORD),
      },
      // Institute owners and admins manage branding, plan, payments and domain.
      ...(user?.isOwner || user?.role === "admin" || user?.is_superuser
        ? [{ id: "settings", label: "Settings", icon: Settings, onSelect: () => navigate(ROUTES.SETTINGS) }]
        : []),
      { id: "sep", label: "", separator: true },
      {
        id: "sign-out",
        label: "Sign out",
        icon: LogOut,
        danger: true,
        onSelect: () => setConfirmOpen(true),
      },
    ],
    [navigate, user?.is_superuser, user?.isOwner, user?.role],
  );

  const header = (
    <div className="fr-menu-head">
      <Avatar size="md" name={name} src={user?.avatar ?? undefined} />
      <div style={{ minWidth: 0 }}>
        <div className="fr-menu-head__name">{name}</div>
        <div className="fr-menu-head__email">{user?.email}</div>
      </div>
    </div>
  );

  return (
    <>
      <Dropdown
        align="end"
        minWidth={248}
        placement={variant === "side" ? "right" : "below"}
        header={header}
        items={items}
        trigger={({ ref, open, toggle }) =>
          variant !== "bar" ? (
            <button
              ref={ref}
              type="button"
              className="fr-account"
              aria-label={`Account menu, signed in as ${name}`}
              aria-haspopup="menu"
              aria-expanded={open}
              onClick={toggle}
            >
              <Avatar size="sm" name={name} src={user?.avatar ?? undefined} />
              <span className="fr-account__text">
                <span className="fr-account__name">{name}</span>
                <span className="fr-account__role">{user?.role ?? ""}</span>
              </span>
              <ChevronsUpDown
                size={15}
                strokeWidth={1.75}
                className="fr-account__chev"
                aria-hidden
              />
            </button>
          ) : (
            <button
              ref={ref}
              type="button"
              className="fr-icon-btn"
              aria-label={`Account menu, signed in as ${name}`}
              aria-haspopup="menu"
              aria-expanded={open}
              onClick={toggle}
            >
              <Avatar size="sm" name={name} src={user?.avatar ?? undefined} />
            </button>
          )
        }
      />
      <SignOutDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} />
    </>
  );
}
